import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store/store";
import { fetchActiveStatusOptions, searchTuitionApplications } from "../../../../store/slices/Tuition/tuitionAdminSlice";
import Dropdown from "../../../../components/common/AdminDropdown";
import CustomDatepicker from "../../../../components/common/Forms/Datepicker";
import ErrorBanner from "../../../../components/common/ErrorBanner";
import Loader from "../../../../components/common/Loader";
import api from "../../../../api/axios";
import endpoints from "../../../../api/endpoints";
import { trackButtonClick } from '../../../../services/analytics';
import { safeGet } from "../../../../utils/safeGet";
import { convertToISOWithTimezone } from "../../../../utils/date";

// Helper to highlight and focus fields for field-and-text errors
const handleFieldAndTextError = (
  selectedSearchField: string,
  searchTerm: string,
  highlightField: (id: string) => void
) => {
  if (selectedSearchField === "Select field") {
    highlightField("searchField");
  }
  if (!searchTerm.trim()) {
    highlightField("search-term-input");
  }
  
  const firstEmptyField = selectedSearchField === "Select field"
    ? document.getElementById("search-field-dropdown")?.querySelector('button.drop-menu-dropdown') as HTMLElement
    : document.getElementById("search-term-input");
  firstEmptyField?.focus();
};

// Helper to highlight and focus fields for date range errors
const handleDateRangeError = (
  fromDate: string,
  toDate: string,
  highlightField: (id: string) => void
) => {
  if (!fromDate) {
    highlightField("date-input-fromDate");
  }
  if (!toDate) {
    highlightField("date-input-toDate");
  }
  
  const firstEmptyField = fromDate
    ? document.getElementById("date-input-toDate")
    : document.getElementById("date-input-fromDate");
  firstEmptyField?.focus();
};

// Helper to highlight and focus fields for last name errors
const handleLastNameError = (
  startingLetter: string,
  throughLetter: string,
  highlightField: (id: string) => void
) => {
  if (!startingLetter.trim()) {
    highlightField("starting-letter-input");
  }
  if (!throughLetter.trim()) {
    highlightField("through-letter-input");
  }
  
  const firstEmptyField = startingLetter.trim()
    ? document.getElementById("through-letter-input")
    : document.getElementById("starting-letter-input");
  firstEmptyField?.focus();
};

interface SearchStatusProps {
  selectedStatus: string;
  onStatusChange: (status: string) => void;
  onViewResults: (results: any[], params: any) => void;
  initialSearch?: any;
}

const TuitionSearchStatus = ({
  selectedStatus,
  onStatusChange,
  onViewResults,
  initialSearch,
}: SearchStatusProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading: reduxLoading } = useSelector((state: RootState) => state.tuitionAdmin);
  
  const [error, setError] = useState<string | null>(null);
  // Field-level errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Status options - fetched from API
  const [statusOptions, setStatusOptions] = useState<string[]>(["Select Status"]);

  // Date Range state
  const [dateType, setDateType] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  // Search by Fields state
  const searchFieldOptions = [
    { id: "", value: "Select field" },
    { id: "EMPLOYEE_TXN.EMP_NUID", value: "Employee NUID" },
    { id: "EMPLOYEE.EMP_LAST_NAME", value: "Last Name" },
    { id: "MGR_NAME", value: "Primary Approver Name" },
    { id: "EMPL_APPLICTN.EMP_ID", value: "Employee ID" },
    { id: "EMPL_APPLICTN.APPLICTN_ID", value: "Application ID" },
    { id: "EMPL_APPLICTN.USER_APPL_ID", value: "User Application ID" },
    { id: "EMPL_APPLICTN.FUNDING_ORG", value: "Funding Source" },
    { id: "EMPL_APPLICTN.SCHOOL_NAME", value: "School Name" },
    { id: "EMPL_APPLICTN_COMMENT.IS_COMMENT", value: "Is Comment" },
  ];
  const [selectedSearchField, setSelectedSearchField] = useState<string>("Select field");
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Last Name Range state
  const [startingLetter, setStartingLetter] = useState<string>("");
  const [throughLetter, setThroughLetter] = useState<string>("");

  // Filter by Fields state
  const [selectedRegion, setSelectedRegion] = useState<string>("Select region");
  const [selectedUnion, setSelectedUnion] = useState<string>("Select Union");

  // Dropdown options from API
  const [regionOptions, setRegionOptions] = useState<any[]>([{ id: "", value: "Select region" }]);
  const [unionOptions, setUnionOptions] = useState<any[]>([{ id: "", value: "Select Union" }]);

  // Function to highlight a field with red border
  const highlightField = (fieldId: string) => {
    console.log(`Attempting to highlight field: ${fieldId}`);
    
    let fieldElement = document.getElementById(fieldId) || 
                       document.getElementById(`date-input-${fieldId}`);
    
    // For "Select field to search by" dropdown, find it by the wrapper ID
    if (!fieldElement && fieldId === "searchField") {
      const wrapper = document.getElementById("search-field-dropdown");
      if (wrapper) {
        // Try common trigger selectors used by AdminDropdown and Forms Dropdown
        fieldElement = (wrapper.querySelector('button.ds-dropdown__trigger-button') as HTMLElement)
                    || (wrapper.querySelector('button.drop-menu-dropdown') as HTMLElement)
                    || (wrapper.querySelector('button.ds-dropdown__label') as HTMLElement)
                    || (wrapper.querySelector('[role="button"]') as HTMLElement)
                    || (wrapper.querySelector('button') as HTMLElement)
                    || (wrapper.querySelector('.ds-dropdown__native-wrap') as HTMLElement)
                    || undefined;
      }
    }
    
    if (fieldElement) {
      console.log(`Found field element for ${fieldId}`, fieldElement);
      
      // Remove any existing highlight and listeners first
      fieldElement.style.borderColor = "";
      fieldElement.style.borderWidth = "";
      fieldElement.style.borderStyle = "";
      fieldElement.classList.remove("error-highlight");
      
      // Use a small timeout to ensure styles are reset before re-applying
      setTimeout(() => {
        // Add red border only to the input field itself
        fieldElement.style.borderColor = "#d32f2f";
        fieldElement.style.borderWidth = "2px";
        fieldElement.style.borderStyle = "solid";
        fieldElement.classList.add("error-highlight");
        
        // Create the removeHighlight function with proper cleanup
        const removeHighlight = () => {
          fieldElement.style.borderColor = "";
          fieldElement.style.borderWidth = "";
          fieldElement.style.borderStyle = "";
          fieldElement.classList.remove("error-highlight");
          
          // Remove event listeners after highlight is removed
          fieldElement.removeEventListener('focus', removeHighlight);
          fieldElement.removeEventListener('click', removeHighlight);
          fieldElement.removeEventListener('input', removeHighlight);
          
          // Also remove from calendar button if it exists
          const calendarButton = fieldElement.parentElement?.querySelector('button.date-input-icon');
          if (calendarButton) {
            calendarButton.removeEventListener('click', removeHighlight);
          }
        };
        
        // Add event listeners to remove highlight on interaction
        fieldElement.addEventListener('focus', removeHighlight, { once: true });
        fieldElement.addEventListener('click', removeHighlight, { once: true });
        fieldElement.addEventListener('input', removeHighlight, { once: true });
        
        // Also add listener to the calendar icon button
        const calendarButton = fieldElement.parentElement?.querySelector('button.date-input-icon');
        if (calendarButton) {
          calendarButton.addEventListener('click', removeHighlight, { once: true });
        }
      }, 10);
      
      // Scroll to the field
      fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      console.warn(`Field element not found for: ${fieldId}`);
    }
  };

  // Function to remove all highlights
  const removeAllHighlights = () => {
    const highlightedElements = document.querySelectorAll('.error-highlight');
    highlightedElements.forEach(element => {
      (element as HTMLElement).style.borderColor = "";
      (element as HTMLElement).style.borderWidth = "";
      (element as HTMLElement).style.borderStyle = "";
      element.classList.remove("error-highlight");
    });
  };

  // Handler for when "View field" is clicked in ErrorBanner
  const handleFieldClick = (fieldName: string) => {
    console.log(`View field clicked for: ${fieldName}`);
    
    if (error?.includes("Select Field and Text")) {
      handleFieldAndTextError(selectedSearchField, searchTerm, highlightField);
    } else if (error?.includes("Date Range")) {
      handleDateRangeError(fromDate, toDate, highlightField);
    } else if (error?.includes("Last Name")) {
      handleLastNameError(startingLetter, throughLetter, highlightField);
    }
  };

  // Effect to highlight fields when error changes
  useEffect(() => {
    removeAllHighlights();
    
    if (error) {
      setTimeout(() => {
        if (error.includes("Select Field and Text")) {
          // Highlight only the empty field(s)
          if (selectedSearchField === "Select field") {
            highlightField("searchField");
          }
          if (!searchTerm.trim()) {
            highlightField("search-term-input");
          }
        } else if (error.includes("Date Range")) {
          // Highlight only the empty date field(s)
          if (!fromDate) {
            highlightField("date-input-fromDate");
          }
          if (!toDate) {
            highlightField("date-input-toDate");
          }
        } else if (error.includes("Last Name")) {
          // Highlight only the empty field(s)
          if (!startingLetter.trim()) {
            highlightField("starting-letter-input");
          }
          if (!throughLetter.trim()) {
            highlightField("through-letter-input");
          }
        }
      }, 100);
    }
  }, [error, fromDate, toDate, selectedSearchField, searchTerm, startingLetter, throughLetter]);

  // Initialize form values from container when returning to search panel
  useEffect(() => {
    if (initialSearch) {
      const setters: { [key: string]: (v: any) => void } = {
        dateType: setDateType,
        fromDate: setFromDate,
        toDate: setToDate,
        selectedSearchField: setSelectedSearchField,
        searchTerm: setSearchTerm,
        startingLetter: setStartingLetter,
        throughLetter: setThroughLetter,
        selectedRegion: setSelectedRegion,
        selectedUnion: setSelectedUnion,
      };
      Object.entries(setters).forEach(([key, setter]) => {
        if (initialSearch[key]) setter(initialSearch[key]);
      });
    }
  }, [initialSearch]);

  // Fetch status, region and union options
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
         // Fetch Active status options
        const activeStatuses = await dispatch(
        fetchActiveStatusOptions("AT001")
      ).unwrap();
        if (activeStatuses && Array.isArray(activeStatuses)) {
          const statuses = activeStatuses.map((item: any) => safeGet(item.statusName, safeGet(item.name, safeGet(item.value, item))));
          setStatusOptions(["Select Status", ...statuses]);
        }

        // Fetch all tuition options (regions and unions)
        const response = await api.get(endpoints.tuition.admin.getTuitionOptions("all", "AT001"));
        
        if (response.data) {
          // Process regions
          if (response.data.regions && Array.isArray(response.data.regions)) {
            setRegionOptions([{ id: "", value: "Select region" }, ...response.data.regions]);
          }

          // Process unions
          if (response.data.unions && Array.isArray(response.data.unions)) {
            setUnionOptions([{ id: "", value: "Select Union" }, ...response.data.unions]);
          }
        }
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
        // Fallback to default status options
        setStatusOptions([
          "Select Status",
          "Submitted",
          "Withdrawn",
          "On hold",
          "Pending",
          "Ready for payment",
        ]);
      }
    };

    fetchDropdownOptions();
  }, []);

  const handleDateChange = (name: string, value: string) => {
    if (name === "fromDate") {
      setFromDate(value);
    } else if (name === "toDate") {
      setToDate(value);
    }
  };

  const handleClearClick = () => {
    onStatusChange("Select Status");
    setDateType("");
    setFromDate("");
    setToDate("");
    setSelectedSearchField("Select field");
    setSearchTerm("");
    setStartingLetter("");
    setThroughLetter("");
    setSelectedRegion("Select region");
    setSelectedUnion("Select Union");
    setError(null);
    setFieldErrors({});
  };

  // Validation helpers
  const dateRegex = /^(0\d|1[0-2])\/(0\d|[12]\d|3[01])\/\d{4}$/;

  const validateFieldAndText = (selectedSearchField: string, searchTerm: string) => {
    const errors: Record<string, string> = {};
    if (selectedSearchField !== "Select field" && !searchTerm.trim()) {
      errors.searchTerm = "Search Term is required when a field is selected";
      return { errors, errorMsg: "Both Select Field and Text have to be entered." };
    }
    if (searchTerm.trim() && (selectedSearchField === "Select field" || selectedSearchField === "")) {
      errors.searchField = "Select Field is required when search term is entered";
      return { errors, errorMsg: "Both Select Field and Text have to be entered." };
    }
    return { errors, errorMsg: null };
  };

  const validateDateRange = (dateType: string, fromDate: string, toDate: string) => {
    const errors: Record<string, string> = {};

    const validateSingleDate = (date: string, field: 'fromDate' | 'toDate') => {
      if (!date) {
        errors[field] = `${field === 'fromDate' ? 'From' : 'To'} Date is required`;
      } else if (!dateRegex.test(date)) {
        errors[field] = `${field === 'fromDate' ? 'From' : 'To'} Date must be a valid date in MM/DD/YYYY format`;
      }
    };

    // Case 1: dateType selected but one or both dates missing
    if (dateType && (!fromDate || !toDate)) {
      validateSingleDate(fromDate, 'fromDate');
      validateSingleDate(toDate, 'toDate');
      return { errors, errorMsg: "Both Date Range Start Date and End Date have to be entered." };
    }

    // Case 2: one date entered, other missing
    if ((fromDate && !toDate) || (!fromDate && toDate)) {
      validateSingleDate(fromDate, 'fromDate');
      validateSingleDate(toDate, 'toDate');
      return { errors, errorMsg: "Both Date Range Start Date and End Date have to be entered." };
    }

    // Case 3: both dates entered
    if (fromDate && toDate) {
      validateSingleDate(fromDate, 'fromDate');
      validateSingleDate(toDate, 'toDate');
      if (Object.keys(errors).some(key => key === 'fromDate' || key === 'toDate')) {
        return { errors, errorMsg: "Date must be in valid MM/DD/YYYY format." };
      }
      // Ensure toDate is not earlier than fromDate
      if (dateRegex.test(fromDate) && dateRegex.test(toDate)) {
        const [fm, fd, fy] = fromDate.split('/').map(Number);
        const [tm, td, ty] = toDate.split('/').map(Number);
        const from = new Date(fy, fm - 1, fd);
        const to = new Date(ty, tm - 1, td);
        if (to.getTime() < from.getTime()) {
          errors.toDate = "To Date must be the same as or after From Date";
          errors.fromDate = "From Date must be the same as or before To Date";
          return { errors, errorMsg: "Start date must be the same as or before the end date." };
        }
      }
    }

    return { errors, errorMsg: null };
  };

  const validateLastNameRange = (startingLetter: string, throughLetter: string) => {
    const errors: Record<string, string> = {};
    const isLetter = (val: string) => /^[a-zA-Z]$/.test(val);

    if (startingLetter.trim() && !isLetter(startingLetter.trim())) {
      errors.startingLetter = "Please enter a letter (A–Z)";
    }
    if (throughLetter.trim() && !isLetter(throughLetter.trim())) {
      errors.throughLetter = "Please enter a letter (A–Z)";
    }
    if (Object.keys(errors).length > 0) {
      return { errors, errorMsg: "Last Name range fields must contain letters only." };
    }

    if ((startingLetter.trim() && !throughLetter.trim()) || (!startingLetter.trim() && throughLetter.trim())) {
      if (!startingLetter.trim()) errors.startingLetter = "Starting Letter is required";
      if (!throughLetter.trim()) errors.throughLetter = "Through Letter is required";
      return { errors, errorMsg: "Both Last Name Start and End letters have to be entered." };
    }
    if (startingLetter && throughLetter && startingLetter.toUpperCase() > throughLetter.toUpperCase()) {
      errors.startingLetter = "Starting Letter cannot be greater than Through Letter";
      return { errors, errorMsg: "Last Name Begin cannot be greater than Last Name End." };
    }
    return { errors, errorMsg: null };
  };

  const handleSearchClick = async () => {
    setError(null);
    setFieldErrors({});
    console.log('📊 Analytics: Search applications clicked');
    trackButtonClick('Search Applications', 'tuition-search-status', { status: selectedStatus });

    setTimeout(() => {
      let errors: Record<string, string> = {};
      let errorMsg: string | null = null;

      // Validate field and text
      const fieldTextResult = validateFieldAndText(selectedSearchField, searchTerm);
      errors = { ...errors, ...fieldTextResult.errors };
      if (fieldTextResult.errorMsg) errorMsg = fieldTextResult.errorMsg;

      // Validate date range
      const dateResult = validateDateRange(dateType, fromDate, toDate);
      errors = { ...errors, ...dateResult.errors };
      if (dateResult.errorMsg) errorMsg = dateResult.errorMsg;

      // Validate last name range
      const lastNameResult = validateLastNameRange(startingLetter, throughLetter);
      errors = { ...errors, ...lastNameResult.errors };
      if (lastNameResult.errorMsg) errorMsg = lastNameResult.errorMsg;

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        if (errorMsg) setError(errorMsg);
        return;
      }

      performSearch();
    }, 0);
  };

  const performSearch = async () => {
    try {
      const params = buildSearchParams();
      console.log("=== TuitionSearchStatus performSearch ===");
      console.log("Built search parameters:", params);
      console.log("Params stringified:", JSON.stringify(params, null, 2));

      // include raw form values so the parent can restore the form when returning to search
      const rawForm = {
        dateType,
        fromDate,
        toDate,
        selectedSearchField,
        searchTerm,
        startingLetter,
        throughLetter,
        selectedRegion,
        selectedUnion,
      };

      const resultAction = await dispatch(searchTuitionApplications(params));
      
      if (searchTuitionApplications.fulfilled.match(resultAction)) {
        const results = resultAction.payload;
        console.log("Search results from Redux:", results);
        console.log("About to call onViewResults with params:", params);
        const searchParams = buildSearchParams();
        onViewResults(Array.isArray(results) ? results : [], { ...searchParams, __rawSearch: rawForm });
      } else {
        const errorMessage = typeof resultAction.payload === "string"
          ? resultAction.payload
          : "Failed to fetch applications";
        setError(errorMessage);
        // Do not transition to results view on error — keep the search form
        // visible so the user can see the error banner and correct their input.
      }
    } catch (error: any) {
      console.error("Error searching applications:", error);
      setError(safeGet(error.message, "Failed to fetch applications"));
      // Do not transition to results view on unexpected error — keep the search
      // form visible so the user can see the error banner.
    }
  };

  const buildSearchParams = () => {
    const params: any = { applType: "AT001" };

    if (selectedStatus && selectedStatus !== "Select Status") {
      params.status = selectedStatus;
    }

    // Helper function to convert MM/DD/YYYY to MM-DD-YYYY
    const convertDateFormat = (dateStr: string): string => {
      if (!dateStr) return '';
      return dateStr.replaceAll('/', '-');
    };

    // Handle date range based on selected date type
    if (dateType && fromDate && toDate) {
      const startDate = convertDateFormat(fromDate);
      const endDate = convertDateFormat(toDate);
      
      if (dateType === "Course start date") {
        params.courseStartDate = startDate;
        params.courseEndDate = endDate;
      } else if (dateType === "Submission date") {
        params.submissionStartDate = startDate;
        params.submissionEndDate = endDate;
      } else if (dateType === "EE document upload") {
         // Use ISO 8601 format: yyyy-MM-ddTHH:mm:ss.SSS±TT:TT
        params.eeDocumentStartDate = convertToISOWithTimezone(fromDate, "start");
        params.eeDocumentEndDate = convertToISOWithTimezone(toDate, "end");
      }
    }

    const selectedFieldOption = searchFieldOptions.find(opt => opt.value === selectedSearchField);
    if (selectedFieldOption?.id && searchTerm) {
      params.selectedField = selectedFieldOption.id;
      params.selectedFieldValue = searchTerm.trim();
    }

    if (startingLetter) {
      params.lastNameStart = startingLetter.toUpperCase().trim();
    }
    if (throughLetter) {
      params.lastNameEnd = throughLetter.toUpperCase().trim();
    }

    const selectedRegionOption = regionOptions.find(opt => opt.value === selectedRegion);
    if (selectedRegionOption?.id) {
      params.regionId = selectedRegionOption.id;
    }

    const selectedUnionOption = unionOptions.find(opt => opt.value === selectedUnion);
    if (selectedUnionOption?.id) {
      params.unionId = selectedUnionOption.id;
    }

    return params;
  };

  return (
    <div>
      {reduxLoading && <Loader />}
      
      {/* Search Applications container */}
      <div className="bg-white border border-gray-200 rounded-xl p-5! mt-4!">
        <h2 className="h2 mb-2">Search Applications</h2>
        <p className="p3 -book mb-8">Search by one or more options.</p>

        {error && (
          <ErrorBanner
            currentStep={1}
            errors={{ searchCriteria: error }}
            fieldLabels={{ searchCriteria: "Search Criteria" }}
            onFieldClick={handleFieldClick}
          />
        )}

        {/* Custom styles for wider dropdowns */}
        <style>{`
          /* Make tuition-dropdown-wide match the width behavior of the search input
             Ensure dropdown takes full width of its grid column/wrapper (like #search-term-input) */
          

        .tuition-dropdown-wide {
            display: block !important;
            width: 100% !important;
            min-width: 0 !important; /* prevent long options from forcing min width */
            max-width: 100% !important;
            box-sizing: border-box !important;
            position: relative !important; /* ensure absolute menu positions against this */
          }
          /* Also ensure the wrapper that contains the dropdown behaves like the input wrapper */
          #search-field-dropdown {
            width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box !important;
            display: block !important;
          }

          /* Ensure the internal drop-menu and the ds-dropdown element stretch to fill wrapper */
          .tuition-dropdown-wide .ds-dropdown,
          .tuition-dropdown-wide .ds-dropdown__drop-menu,
          .tuition-dropdown-wide .ds-dropdown__native-wrap {
            position: relative !important;
            width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box !important;
            display: block !important;
          }

          /* Make trigger and native wrap use full width and flex layout so it matches the input behavior */
          .tuition-dropdown-wide .ds-dropdown__native-wrap,
          .tuition-dropdown-wide .ds-dropdown__trigger-button,
          .tuition-dropdown-wide .ds-dropdown__trigger-text,
          .tuition-dropdown-wide .drop-menu-dropdown,
          .tuition-dropdown-wide button.drop-menu-dropdown,
          .tuition-dropdown-wide .drop-menu {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          /* Force trigger button to be flex and full width */
          .tuition-dropdown-wide .ds-dropdown__trigger-button {
            display: flex !important;
            align-items: center !important;
            width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box !important;
            padding: 0.5rem 0.75rem !important;
          }

          /* Ensure the visible menu aligns to trigger and fills its width */
          .tuition-dropdown-wide .ds-dropdown__menu-list,
          .tuition-dropdown-wide .drop-menu__list,
          .tuition-dropdown-wide .drop-menu__items {
            min-width: 100% !important;
            left: 0 !important;
            right: auto !important;
            box-sizing: border-box !important;
            max-height: 400px !important;
            overflow-y: auto !important;
            position: absolute !important;
          }

          .tuition-dropdown-wide .ds-dropdown__menu-list li,
          .tuition-dropdown-wide .ds-dropdown__menu-list button,
          .tuition-dropdown-wide .drop-menu__list li,
          .tuition-dropdown-wide .drop-menu__list button {
            width: 100% !important;
            box-sizing: border-box !important;
          }

          /* Make native select full width (fallback) */
          .tuition-dropdown-wide select {
            width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box !important;
          }

          /* Keep trigger text left aligned and chevron to right */
          .tuition-dropdown-wide .ds-dropdown__trigger-text {
            text-align: left !important;
            justify-content: flex-start !important;
            overflow: hidden !important;
            white-space: nowrap !important;
            text-overflow: ellipsis !important;
            flex: 1 1 auto !important;
            min-width: 0 !important;
          }
          .tuition-dropdown-wide .ds-dropdown__trigger-button {
            text-align: left !important;
            justify-content: space-between !important;
          }

        `}</style>

        {/* GRID — matches screenshot columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-[60px]">
          {/* Filter by Status */}
          <div>
            <h3 className="h3 mb-3" style={{ color: "#003B71" }}>
              Filter by Status
            </h3>
            <Dropdown
              label="Application Status"
              selectedValue={selectedStatus}
              options={statusOptions}
              onSelect={onStatusChange}
              className="tuition-dropdown-wide"
            />
            <hr className="-variation-1 !my-[40px] " />
             {/* Last Name Range */}
          <div>
            <h3 className="h3 mb-3" style={{ color: "#003B71" }}>
              Search by Last Name Range
            </h3>
            <div className="flex">
              <div
                className="ds-form"
                data-ds-theme="vessel"
                data-ds-variant="input"
                data-ds-version="1"
              >
                <label htmlFor="starting-letter-input">Starting with letter:</label>
                <input
                  autoComplete="on"
                  className={`w-full min-w-0 block ${fieldErrors.startingLetter ? 'field-error' : ''}`}
                  id="starting-letter-input"
                  type="text"
                  maxLength={1}
                  value={startingLetter}
                  onChange={(e) => setStartingLetter(e.target.value.toUpperCase())}
                  style={{ width: "80%" }}
                />
                {fieldErrors.startingLetter && (
                  <div className="error-description">
                    <ul id="startingLetter-error-description-list-1">
                      <li>
                        <img
                          src="/assets/images/errorCircleSmall.svg"
                          alt=""
                          width="14"
                          height="14"
                        />
                        <span className="screenreader-only">Error</span>
                        {fieldErrors.startingLetter}
                      </li>
                    </ul>
                  </div>
                )}
              </div>
              <div
                className="ds-form"
                data-ds-theme="vessel"
                data-ds-variant="input"
                data-ds-version="1"
              >
                <label htmlFor="through-letter-input">Through letter:</label>
                <input
                  autoComplete="on"
                  className={`w-full min-w-0 block ${fieldErrors.throughLetter ? 'field-error' : ''}`}
                  id="through-letter-input"
                  type="text"
                  maxLength={1}
                  value={throughLetter}
                  onChange={(e) => setThroughLetter(e.target.value.toUpperCase())}
                  style={{ width: "80%" }}
                />
                {fieldErrors.throughLetter && (
                  <div className="error-description">
                    <ul id="throughLetter-error-description-list-1">
                      <li>
                        <img
                          src="/assets/images/errorCircleSmall.svg"
                          alt=""
                          width="14"
                          height="14"
                        />
                        <span className="screenreader-only">Error</span>
                        {fieldErrors.throughLetter}
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>

          {/* Date Range */}
          <div>
            <h3 className="h3 mb-3" style={{color:'#003B71'}}>Date Range</h3>
            <p className="p4 -medium">Select what type of dates to search by:</p>
            <div className="space-y-2 mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="dateType"
                  value="Course start date"
                  checked={dateType === "Course start date"}
                  onChange={(e) => setDateType(e.target.value)}
                />
                <span className="p3">Course start date</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="dateType"
                  value="Submission date"
                  checked={dateType === "Submission date"}
                  onChange={(e) => setDateType(e.target.value)}
                />
                <span className="p3">Submission date</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="dateType"
                  value="EE document upload"
                  checked={dateType === "EE document upload"}
                  onChange={(e) => setDateType(e.target.value)}
                />
                <span className="p3">EE document upload</span>
              </label>
            </div>

            <div className="flex gap-3 !mt-[18px]">
              <div className="w-full">
                <CustomDatepicker
                  name="fromDate"
                  label="From"
                  value={fromDate}
                  onChange={handleDateChange}
                  helperText={fieldErrors.fromDate ? "" : "MM/DD/YYYY"}
                  error={fieldErrors.fromDate}
                  disabled={!dateType}
                />
              </div>
              <div className="w-full">
                <CustomDatepicker
                  name="toDate"
                  label="To"
                  value={toDate}
                  onChange={handleDateChange}
                  helperText={fieldErrors.toDate ? "" : "MM/DD/YYYY"}
                  error={fieldErrors.toDate}
                  disabled={!dateType}
                />
              </div>
            </div>
          </div>

          {/* Search by Fields */}
          <div>
            <h3 className="h3 mb-3" style={{color:'#003B71'}}>Search by Fields</h3>
            <div id="search-field-dropdown">
              <Dropdown
                label="Select field to search by"
                selectedValue={selectedSearchField}
                options={searchFieldOptions.map(option => option.value)}
                onSelect={setSelectedSearchField}
                className="tuition-dropdown-wide"
              />
            </div>
            <div
              className="ds-form mt-4 w-full"
              data-ds-theme="vessel"
              data-ds-variant="input"
              data-ds-version="1"
            >
              <label htmlFor="search-term-input">Search Term</label>
              <input
                autoComplete="on"
                className={`w-full min-w-0 block ${fieldErrors.searchTerm ? 'field-error' : ''}`}
                id="search-term-input"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: "100%" }}
              />
              {fieldErrors.searchTerm && (
                <div className="error-description">
                  <ul id="searchTerm-error-description-list-1">
                    <li>
                      <img
                        src="/assets/images/errorCircleSmall.svg"
                        alt=""
                        width="14"
                        height="14"
                      />
                      <span className="screenreader-only">Error</span>
                      {fieldErrors.searchTerm}
                    </li>
                  </ul>
                </div>
              )}
              <hr className="-variation-1 !my-[40px] " />
                {/* Filter by Fields */}
          <div>
            <h3 className="h3 mb-3" style={{color:'#003B71'}}>Filter by Fields</h3>
            <Dropdown
              label="Region"
              selectedValue={selectedRegion}
              options={regionOptions.map(option => option.value)}
              onSelect={setSelectedRegion}
              className="tuition-dropdown-wide"
            />
            <div className="mt-4">
              <Dropdown
                label="Union"
                selectedValue={selectedUnion}
                options={unionOptions.map(option => option.value)}
                onSelect={setSelectedUnion}
                className="tuition-dropdown-wide"
              />
            </div>
          </div>
            </div>
          </div>

        
        </div>

        {/* Actions — matches bottom-left alignment */}
        <hr className="-variation-1 !my-[40px] " />
        <div className="flex gap-4 mt-10">
          <button
            type="button"
            className="button -secondary"
            onClick={handleClearClick}
          >
            Clear
          </button>

          <button
            type="button"
            className="button -primary -light"
            data-analytics-type="button"
            data-analytics-click="Search"
            onClick={handleSearchClick}
          >
            Search
          </button>
        </div>
      </div>
    </div>
  );
};
export default TuitionSearchStatus;
